import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate } from "src/utils/dateTime";
import { HBox, PanelBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { DatePickerField, toUTCISODate } from "src/view/conponents/DateRangePicker";
import { Error } from "@ui/error/Error";
import { IconArrowDown } from "@ui/icons/IconArrowDown";
import { IconArrowRight } from "@ui/icons/IconArrowRight";
import { IconEdit } from "@ui/icons/IconEdit";
import { AnimatedCollapse } from "@ui/panel/AnimatedCollapse";
import { ProviderNormalPanel as NormalPanel } from "./providers/ProviderPanel";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { Signal } from "badmfck-signal";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import { notify } from "../tools/notify";
import { ChannelRemoteItem, ChannelsList } from "./channelList/Channels";
import { FormParser } from "./FormParser";
import { S_CONFIRM } from "./popup/general/ConfirmDialog";
import { NoRecords } from "./providers/NoRecords";

export type TFeesType = "rollings" | "rates" | "setup" | "limits" | "settings";
export type TFeesTargetType = "client" | "merchant" | "provider";

export interface TFeesModel {
	id: number;
	client_id: number;
	created_at?: string | number;
	activated_from: string | number;
	currency: string;
	data: any; // Зависит от типа комиссии
	type: TFeesType;
	product_id?: number;
	product_type?: string;
	product_subtype?: string;
	project_id?: number;
	created_by?: number;
	updated_by?: number;
}

export const Fees = ({ target_id, type, target_type, headless }: { target_id: number; type: TFeesType; target_type: TFeesTargetType; headless?: boolean }) => {
	useLocale();
	const [data, request, busy] = useAPI<any>({
		method: "fees/get",
		data: {
			target_id: target_id,
			type: type,
			target_type: target_type,
		},
	});

	const noData = data?.data && data.data.length === 0 && busy === false;

	//@ts-ignore
	const handleFeeChange = async (feeData: any, key: string, value: any) => {
		if (!data?.data) return;
		const updatedItems = data?.data.map((item: any) => (item === feeData ? { ...item, [key]: value } : item));
		const response = await GD.REQ_API.request({
			method: "fees/set",
			data: { ...data, data: updatedItems },
		});
		if (response.error) {
			notify.error(
				() => (
					<VBox gap="8px">
						<Typo variant="text">{response.error.message}</Typo>
						{response.error.details && <Typo variant="medium">{response.error.details}</Typo>}
					</VBox>
				)
			);
			return;
		}
		request();
	};

	console.log(handleFeeChange, "handleFeeChange");

	let header = null;
	switch (type) {
		case "rollings":
			header = (
				<FeesHeader line1={msg("rollingReserveConfiguration")} line2={msg("generalClientRollingReserveConfigurationAppliesToClientForEachCur")} />
			);
			break;
		case "rates":
			header = <FeesHeader line1={msg("ratesConfiguration")} line2={msg("generalClientRatesConfigurationAppliesToClientForEachCurrencyAndP")} />;
			break;
		case "setup":
			header = <FeesHeader line1={msg("clientSetupConfiguration")} line2={msg("generalClientSetupConfigurationAppliesToClient")} />;
			break;
		default:
			header = <FeesHeader line1={msg("feesConfiguration")} />;
	}

	return (
		<>
			<ProgressBar busy={busy} />
			{!headless && header}
			<Spacer variant="normal" />
			{!noData && data?.data.map((val: any, index: number) => <FeeItem key={val.id + "" + index} data={val} onChange={request} />)}
			{noData && <NoRecords line1={msg("ui_87b27ec96e")} line2={msg("ui_627d084faa")} />}
			<Spacer variant="tiny" />
			<HBox>
				<Spacer grow />
				<Button variant="secondarySmall" onClick={() => S_ADD_FEES_POPUP.invoke({ target_id, type, target_type, onClose: request })} disabled={busy}>{msg("addFeeConfiguration")}</Button>
			</HBox>
		</>
	);
};

const FeesHeader = ({ line1, line2 }: { line1: string; line2?: string }) => {
	useLocale();
	return (
		<>
			<Typo variant="headMedium">{line1}</Typo>
			{line2 && <Typo variant="caption">{line2}</Typo>}
		</>
	);
};

export const FeesRates = (req: { target_id: number; target_type: string }) => {
	useLocale();
	// get all merchants
	const [merchants, _, busy] = useAPI<any>({
		method: "clients/group/list",
		data: {
			client_id: req.target_id,
			status: "active",
		},
	});

	return (
		<>
			<ProgressBar busy={busy} />
			<FeesHeader line1={msg("ratesConfiguration")} line2={msg("ratesConfigurationAppliesToEachMerchantPaymentChannel")} />
			<Spacer />
			<VBox gap="5px">
				{merchants?.data?.map((merchant: IClientGroup) => (
					<MerchantItem merchant={merchant} key={merchant.id} />
				))}
			</VBox>
		</>
	);
};

const MerchantItem = ({ merchant }: { merchant: IClientGroup }) => {
	useLocale();
	const [expanded, setExpanded] = useState<boolean>(false);

	return (
		<>
			<PanelBox
				variant="strict"
				onClick={() => {
					setExpanded(!expanded);
				}}>
				<HBox key={merchant.id} gap="10px" variant="left-center">
					<Typo variant="medium" bold>
						{merchant.name}
					</Typo>
					<Spacer grow />
					<IconArrowDown rotated={expanded} />
				</HBox>
			</PanelBox>

			<AnimatedCollapse expanded={expanded}>
				<HBox>
					<Spacer />
					<ChannelsList asMerchant={{ merchant_id: merchant.id }} />
				</HBox>
			</AnimatedCollapse>
		</>
	);
};

const FeeItem = ({ data, onChange }: { data: any; onChange: () => void }) => {
	useLocale();
	const [expanded, setExpanded] = useState(!data.historical);
	const [editingDate, setEditingDate] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Date>(() => new Date(data.activated_from));

	const onFeeChange = async (key: string, value: any) => {
		const response = await GD.REQ_API.request({
			method: "fees/set",
			data: { ...data, [key]: value },
		});
		if (response.error) {
			notify.error(
				() => (
					<VBox gap="8px">
						<Typo variant="text">{response.error.message}</Typo>
						{response.error.details && <Typo variant="medium">{response.error.details}</Typo>}
					</VBox>
				)
			);
			return;
		}
		onChange();
		//onFeeChange(data, key, value);
	};

	return (
		<VBox>
			<PanelBox
				variant="light"
				style={{
					cursor: "pointer",
				}}
				onClick={() => {
					setExpanded(!expanded);
				}}>
				<HBox variant="left-center">
					<Typo variant="medium">{data.historical ? msg("historicalSetup") : msg("activatedFrom")}</Typo>
					<HBox
						variant="left-center"
						gap="4px"
						onClick={e => {
							if (data.locked) return;
							e?.stopPropagation();
							setEditingDate(true);
						}}
						sx={
							!data.locked
								? {
										cursor: "pointer",
										"&:hover": { opacity: 0.7 },
									}
								: undefined
						}>
						<Typo variant="medium" bold>
							{/* //!TODO: locale; */}
							<DateTimeTooltip value={data.activated_from}>{formatDisplayDate(data.activated_from, { locale: "en-US", format: { weekday: "short", month: "short", day: "2-digit", year: "numeric" } })}</DateTimeTooltip>
						</Typo>
						{!data.locked && <IconEdit color="#505050" width={16} height={16} />}
					</HBox>
					{data.locked && !data.editable && (
						<>
							<IconArrowRight height="10px" width="10px" />
							<Typo variant="small">{msg("locked")}</Typo>
						</>
					)}
					{!data.historical && !data.locked && (
						<>
							<Button
								variant="dangerSmall"
								onClick={async e => {
									e.stopPropagation();
									S_CONFIRM.invoke({
										title: msg("deleteFeeConfiguration"),
										message: msg("areYouSureYouWantToDeleteThisFeeConfiguration"),
										onResult: async val => {
											if (val === "confirm") {
												const result = await GD.REQ_API.request({
													method: "fees/delete",
													data: { id: data.id, target_id: data.target_id, type: data.type, target_type: data.target_type },
												});

												if (result.error) {
									notify.error(
														() => (
															<VBox gap="8px">
																<Typo variant="text">{result.error.message}</Typo>
																{result.error.details && <Typo variant="medium">{result.error.details}</Typo>}
															</VBox>
										)
													);
													return;
												}

								notify.success(msg("feeConfigurationDeleted"));
												onChange();
												//onFeeChange(data, key, value);
											}
										},
									});
								}}>{msg("delete")}</Button>
						</>
					)}
					<Spacer grow />
					<IconArrowDown rotated={expanded} />
				</HBox>
			</PanelBox>

			<AnimatedCollapse expanded={expanded}>
				<Spacer variant="tiny" />
				<PanelBox variant="outlined" style={{ backgroundColor: data.historical ? "#eff5ff" : undefined }}>
					<FormParser
						size="small"
						grow
						previewMode={!data.editable}
						key={data.type + data.activated_from + data.id}
						secondaryButton={null}
						onSubmit={async (d: any): Promise<any> => {
							data.data = d;
							const response = await GD.REQ_API.request({
								method: "fees/set",
								data: data,
							});
							if (response.error) return response.error;
							return data;
						}}
						onRequest={async (): Promise<any> => {
							return data;
						}}
					/>
				</PanelBox>
			</AnimatedCollapse>

			{editingDate && (
				<NormalPanel label={msg("changeActivationDate")} sx={{ width: "400px" }} decorator="absolute" onClose={() => setEditingDate(false)}>
					<VBox gap="12px">
						<DatePickerField size="medium" label={msg("activatedFrom_72d1f309")} value={toUTCISODate(selectedDate)} onChange={value => setSelectedDate(new Date(value))} />
						<HBox sx={{ justifyContent: "flex-end" }} gap="8px">
							<Button variant="secondary" onClick={() => setEditingDate(false)}>{msg("cancel")}</Button>
							<Button
								onClick={() => {
									const formatted = selectedDate.toISOString().split("T")[0];
									setEditingDate(false);
									onFeeChange("activated_from", formatted);
								}}>{msg("save")}</Button>
						</HBox>
					</VBox>
				</NormalPanel>
			)}
		</VBox>
	);
};

const S_ADD_FEES_POPUP = new Signal<{ target_id: number; type: TFeesType; target_type: TFeesTargetType; onClose: () => void } | null>();

export const AddFeesPopup = () => {
	useLocale();
	const request = S_ADD_FEES_POPUP.use();
	const [generatedFees, setGeneratedFees] = useState<any>(null);
	const [busy, setBusy] = useState(false);
	useEffect(() => {
		if (!request) return;
		generateRecord();
	}, [request]);

	if (!request) return null;

	const generateRecord = async () => {
		setBusy(true);
		const res = await GD.REQ_API.request({
			method: "fees/generate",
			data: {
				target_id: target_id,
				target_type: target_type,
				type: type,
				ignore_fees_copy: true,
			},
		});
		setBusy(false);
		setGeneratedFees(res);
	};

	const { target_id, type, target_type } = request;
	const close = () => {
		setGeneratedFees(null);
		setBusy(false);
		S_ADD_FEES_POPUP.invoke(null);
		if (request.onClose) request.onClose();
	};

	let line2 = "For: " + target_type.charAt(0).toUpperCase() + target_type.slice(1) + " ID: " + target_id;
	if (target_type === "merchant") {
		line2 = msg("forMerchantChannelWithId") + target_id;
	}

	return (
		<NormalPanel label={msg("addFeesConfiguration")} sx={{ minWidth: "700px", height: "90vh", backgroundColor: "#eef0f2" }} decorator="absolute" onClose={close}>
			<ProgressBar busy={busy} />
			{generatedFees?.error && <Error error={generatedFees.error} />}
			<FeesHeader line1={msg("templateNewConfiguration_e81d01c9", { v0: type })} line2={line2} />
			<Spacer variant="tiny" />
			<ChannelRemoteItem type={target_type} id={target_id} />
			<Spacer variant="normal" />
			{generatedFees && !generatedFees.error && (
				<>
					<FormParser
						size="small"
						primaryButton={null}
						secondaryButton={null}
						onChange={(data: any) => {
							generatedFees.data.activated_from = data.setup.activated_from;
						}}
						onRequest={async () => {
							return {
								setup: {
									activated_from: generatedFees.data.activated_from,
									["$__activated_from_type"]: "date",
									currency: generatedFees.data.currency,
									["$__currency_disabled"]: true,
									/*id: generatedFees.data.id,
									["$__id_disabled"]: true,*/
								},
								["$__setup_decorator"]: {
									blocks: [
										{
											order: ["activated_from", "currency", "id"],
											gridTemplateColumns: "150px 90px max-content",
										},
									],
								},
							};
						}}
						onSubmit={async () => {}}
					/>
					<Spacer variant="tiny" />
					<FormParser
						scrollable
						size="small"
						key={"fees_add" + target_id + type + target_type + generatedFees.data.id}
						secondaryButton={{
							title: msg("cancel"),
							onClick: close,
						}}
						primaryButton={{
							title: msg("add"),
						}}
						onSubmit={async (data: any): Promise<any> => {
							const response = await GD.REQ_API.request({
								method: "fees/set",
								data: data,
							});
							if (response.error) return response.error;
							close();
							return response;
						}}
						onRequest={async () => {
							return generatedFees;
						}}
					/>
				</>
			)}
		</NormalPanel>
	);
};
